import { Button, LoadingPane, Pane, PaneMenu } from '@folio/stripes/components';
import { IfPermission, TitleManager, useStripes } from '@folio/stripes/core';
import React, { useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Route, Switch, useHistory, useRouteMatch } from 'react-router-dom';
import SortableMultiColumnList from '../components/SortableMultiColumnList';
import useDataRepository from '../data/useDataRepository';
import permissions from '../types/permissions';
import { dateFromYYYYMMDD, getLocalizedDate, isBetweenDatesByDay } from '../utils/DateUtils';
import getStatus from '../utils/getCurrentStatus';
import { useLocaleWeekdays } from '../utils/WeekdayUtils';
import CreateEditCalendarLayer from './CreateEditCalendarLayer';
import InfoPane from './panes/InfoPane';
export var CurrentAssignmentView = function () {
    var _a, _b, _c, _d, _f;
    var intl = useIntl();
    var localeWeekdays = useLocaleWeekdays(intl);
    var dataRepository = useDataRepository();
    var stripes = useStripes();
    var showCreateLayerButtonRef = useRef(null);
    var history = useHistory();
    var currentRouteId = (_b = (_a = useRouteMatch('/settings/calendar/active/:servicePointId')) === null || _a === void 0 ? void 0 : _a.params) === null || _b === void 0 ? void 0 : _b.servicePointId;
    if (!dataRepository.isLoaded()) {
        return (React.createElement(LoadingPane, { paneTitle: React.createElement(FormattedMessage, { id: "ui-calendar.currentAssignmentView.title" }) }));
    }
    var rows = dataRepository.getServicePoints().map(function (servicePoint) {
        var calendars = dataRepository.getCalendars().filter(function (calendar) {
            return (isBetweenDatesByDay(new Date(), dateFromYYYYMMDD(calendar.startDate), dateFromYYYYMMDD(calendar.endDate)) && calendar.assignments.includes(servicePoint.id));
        });
        if (calendars.length === 0) {
            return {
                servicePoint: servicePoint.name.concat(servicePoint.inactive ? ' (inactive)' : ''),
                servicePointId: servicePoint.id,
                calendarName: (React.createElement("div", { style: { fontStyle: 'italic', color: 'grey' } },
                    React.createElement(FormattedMessage, { id: "ui-calendar.currentAssignmentView.noCalendar" }))),
                startDate: '',
                startDateObj: undefined,
                endDate: '',
                endDateObj: undefined,
                currentStatus: intl.formatMessage({
                    id: 'ui-calendar.currentStatus.closed.noNext'
                }),
                calendar: null
            };
        }
        return {
            servicePointId: servicePoint.id,
            servicePoint: servicePoint.name.concat(servicePoint.inactive ? ' (inactive)' : ''),
            calendarName: calendars[0].name,
            startDate: getLocalizedDate(intl, calendars[0].startDate),
            startDateObj: dateFromYYYYMMDD(calendars[0].startDate),
            endDate: getLocalizedDate(intl, calendars[0].endDate),
            endDateObj: dateFromYYYYMMDD(calendars[0].endDate),
            currentStatus: getStatus(intl, localeWeekdays, new Date(), calendars[0]),
            calendar: calendars[0]
        };
    });
    var calendarName = (_d = (_c = dataRepository.getCalendars().filter(function (c) { return c.assignments.includes(currentRouteId !== null && currentRouteId !== void 0 ? currentRouteId : ''); })[0]) === null || _c === void 0 ? void 0 : _c.name) !== null && _d !== void 0 ? _d : '';
    var pageTitle = intl.formatMessage({ id: 'ui-calendar.meta.titleSettings' }) + ' - ' + intl.formatMessage({
        id: 'ui-calendar.currentAssignmentView.title'
    }) + (calendarName ? " - ".concat(calendarName) : '');
    return (React.createElement(TitleManager, { page: pageTitle, stripes: stripes },
        React.createElement(Pane, { paneTitle: React.createElement(FormattedMessage, { id: "ui-calendar.currentAssignmentView.title" }), defaultWidth: currentRouteId === undefined ? 'fill' : '20%', lastMenu: React.createElement(IfPermission, { perm: permissions.CREATE },
                React.createElement(PaneMenu, null,
                    React.createElement(Button, { buttonStyle: "primary", marginBottom0: true, ref: showCreateLayerButtonRef, to: "/settings/calendar/active/create" },
                        React.createElement(FormattedMessage, { id: "ui-calendar.currentAssignmentView.actions.new" })))) },
            React.createElement(SortableMultiColumnList, { sortedColumn: "servicePoint", sortDirection: "ascending", dateColumns: ['startDate', 'endDate'], dateColumnMap: { startDate: 'startDateObj', endDate: 'endDateObj' }, columnMapping: {
                    servicePoint: (React.createElement(FormattedMessage, { id: "ui-calendar.currentAssignmentView.column.servicePoint" })),
                    calendarName: (React.createElement(FormattedMessage, { id: "ui-calendar.currentAssignmentView.column.name" })),
                    startDate: (React.createElement(FormattedMessage, { id: "ui-calendar.currentAssignmentView.column.startDate" })),
                    endDate: (React.createElement(FormattedMessage, { id: "ui-calendar.currentAssignmentView.column.endDate" })),
                    currentStatus: intl.formatMessage({
                        id: 'ui-calendar.currentAssignmentView.column.currentStatus'
                    })
                }, contentData: rows, rowMetadata: [
                    'servicePointId',
                    'calendar',
                    'startDateObj',
                    'endDateObj'
                ], isSelected: function (_a) {
                    var item = _a.item;
                    return (currentRouteId !== undefined &&
                        item.servicePointId === currentRouteId);
                }, onRowClick: function (_e, info) {
                    if (info.startDate === '' ||
                        info.servicePointId === currentRouteId) {
                        // no cal assigned or being toggled off
                        history.push('/settings/calendar/active/');
                    }
                    else {
                        // new cal
                        history.push("/settings/calendar/active/".concat(info.servicePointId));
                    }
                } })),
        React.createElement(Switch, null,
            React.createElement(Route, { path: "/settings/calendar/active/create", render: function (_a) {
                    var location = _a.location;
                    return (React.createElement(IfPermission, { perm: permissions.CREATE },
                        React.createElement(CreateEditCalendarLayer, { dataRepository: dataRepository, initialValue: dataRepository.getCalendar(new URLSearchParams(location.search).get('source')), onClose: function (id) {
                                var _a;
                                if (id === void 0) { id = ''; }
                                history.push("/settings/calendar/active/".concat(id));
                                (_a = showCreateLayerButtonRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                            } })));
                } }),
            React.createElement(Route, { path: "/settings/calendar/active/edit/:id", render: function (_a) {
                    var match = _a.match;
                    return (React.createElement(IfPermission, { perm: permissions.UPDATE },
                        React.createElement(CreateEditCalendarLayer, { dataRepository: dataRepository, initialValue: dataRepository.getCalendar(match.params.id), isEdit: true, onClose: function () {
                                var _a;
                                history.push("/settings/calendar/active/".concat(match.params.id));
                                (_a = showCreateLayerButtonRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                            } })));
                } }),
            React.createElement(Route, { path: "/settings/calendar/active/:id" },
                React.createElement(InfoPane, { editBasePath: "/settings/calendar/active/edit", creationBasePath: "/settings/calendar/active/create", onClose: function () {
                        history.push('/settings/calendar/active/');
                    }, calendar: (_f = rows.filter(function (row) { return row.servicePointId === currentRouteId; })[0]) === null || _f === void 0 ? void 0 : _f.calendar, dataRepository: dataRepository })))));
};
export default CurrentAssignmentView;
